import { useEffect, useMemo, useState } from 'react';
import { scenePresentation } from '../lib/scene-stage.js';
import SceneProps from './SceneProps.jsx';
import NatalObjects from './NatalObjects.jsx';
import CareerObjects from './CareerObjects.jsx';
import { AgeObjects, RelationshipObjects, ActionObjects } from './ContextObjects.jsx';
import './life-scenes.css';
import './scene-diorama.css';

// 12지지 순서 (자 축 인 묘 진 사 오 미 신 유 술 해) → 파일 이름
const BRANCH_KEYS = ['ja', 'chuk', 'in', 'myo', 'jin', 'sa', 'o', 'mi', 'sin', 'yu', 'sul', 'hae'];
// 10천간 순서 (갑 을 병 정 무 기 경 신 임 계) → 날씨 파일
const WEATHER_KEYS = ['gap', 'eul', 'byeong', 'jeong', 'mu', 'gi', 'gyeong', 'sin', 'im', 'gye'];
const SEASON = { in: 'spring', myo: 'spring', jin: 'spring', sa: 'summer', o: 'summer', mi: 'summer', sin: 'autumn', yu: 'autumn', sul: 'autumn', hae: 'winter', ja: 'winter', chuk: 'winter' };
// 가장자리 비네팅 색 (계절). 풍경이 없을 때의 바탕 그라데이션도 같이 쓴다
const SEASON_STYLE = {
  spring: { vig: '70,95,45', bg: 'linear-gradient(#cfe8c8,#8fc07a)' },
  summer: { vig: '20,70,50', bg: 'linear-gradient(#bfe3d6,#4f9a78)' },
  autumn: { vig: '95,45,15', bg: 'linear-gradient(#f2d9a8,#c98a4a)' },
  winter: { vig: '35,55,90', bg: 'linear-gradient(#cfd9ea,#8fa0bd)' },
};
// 시지(태어난 시간대) → 하늘 분위기. 시를 모르면 낮(오)
const TIME_LOOK = [
  /* 자 */ { bright: 0.5, sat: 0.85, tint: '20,30,90,.35', sky: 'night' },
  /* 축 */ { bright: 0.5, sat: 0.85, tint: '20,30,90,.35', sky: 'night' },
  /* 인 */ { bright: 0.72, sat: 0.9, tint: '120,90,140,.22', sky: 'dawn' },
  /* 묘 */ { bright: 0.92, sat: 1, tint: '255,200,170,.16', sky: null },
  /* 진 */ { bright: 1, sat: 1, tint: '255,255,255,0', sky: null },
  /* 사 */ { bright: 1.02, sat: 1, tint: '255,255,255,0', sky: null },
  /* 오 */ { bright: 1.05, sat: 1.02, tint: '255,255,255,0', sky: null },
  /* 미 */ { bright: 1, sat: 1, tint: '255,235,200,.08', sky: null },
  /* 신 */ { bright: 0.97, sat: 1, tint: '255,190,120,.15', sky: null },
  /* 유 */ { bright: 0.88, sat: 1, tint: '255,120,60,.26', sky: 'sunset' },
  /* 술 */ { bright: 0.65, sat: 0.9, tint: '60,40,110,.34', sky: 'dusk' },
  /* 해 */ { bright: 0.5, sat: 0.85, tint: '20,30,90,.35', sky: 'night' },
];
// 일간 → 동물 색 (오행): 목 초록 / 화 빨강 / 토 노랑 / 금 흰색 / 수 검정
const ANIMAL_COLOR = [
  { body: '#7fcf78', light: '#d4f5c4' }, { body: '#7fcf78', light: '#d4f5c4' },   // 갑 을
  { body: '#f0584c', light: '#ffb8a8' }, { body: '#f0584c', light: '#ffb8a8' },   // 병 정
  { body: '#f2c53d', light: '#fff0a6' }, { body: '#f2c53d', light: '#fff0a6' },   // 무 기
  { body: '#f4f2ef', light: '#ffffff' }, { body: '#f4f2ef', light: '#ffffff' },   // 경 신
  { body: '#34343c', light: '#6b6b78' }, { body: '#34343c', light: '#6b6b78' },   // 임 계
];
// 원숭이 SVG 는 색이 고정(빨강)이라 필터로 근사한다
const MONKEY_FILTER = ['hue-rotate(110deg)', 'hue-rotate(110deg)', 'none', 'none', 'hue-rotate(48deg) saturate(1.1)', 'hue-rotate(48deg) saturate(1.1)',
  'grayscale(.9) brightness(1.55)', 'grayscale(.9) brightness(1.55)', 'grayscale(1) brightness(.42)', 'grayscale(1) brightness(.42)'];

const svgCache = new Map();
const base = process.env.PUBLIC_URL || '';

function useSvg(key) {
  const [svg, setSvg] = useState(svgCache.get(key) ?? '');
  useEffect(() => {
    if (svgCache.has(key)) { setSvg(svgCache.get(key)); return undefined; }
    let alive = true;
    fetch(`${base}/assets/animals/${key}.svg`).then((r) => r.text()).then((t) => {
      svgCache.set(key, t); if (alive) setSvg(t);
    }).catch(() => {});
    return () => { alive = false; };
  }, [key]);
  return svg;
}

function stars(seed) {
  let a = seed >>> 0;
  const rnd = () => { a = (Math.imul(a, 1664525) + 1013904223) >>> 0; return a / 4294967296; };
  return Array.from({ length: 46 }, () => ({ x: rnd() * 100, y: rnd() * 34, r: 1 + rnd() * 2, o: 0.5 + rnd() * 0.5 }));
}

/**
 * 합성 장면: 월지 풍경 + 월간 날씨 + 시지 시간대 + 계절 비네팅 + 일지 동물(일간 오행 색)
 * saju: 서버 /api/saju 응답. fixed=true 면 화면 전체 배경
 */
export default function SceneView({ saju, fixed = false, portrait = false, run = null }) {
  const mb = BRANCH_KEYS[saju.month.branch], ms = saju.month.stem;
  const db = BRANCH_KEYS[saju.day.branch], ds = saju.day.stem;
  const look = TIME_LOOK[saju.hour ? saju.hour.branch : 6];
  const season = SEASON[mb], ss = SEASON_STYLE[season];
  const [sceneOk, setSceneOk] = useState(true);
  const [viewId, setViewId] = useState(null);
  const svg = useSvg(db);
  const star = useMemo(() => stars(saju.month.branch * 31 + saju.day.stem), [saju.month.branch, saju.day.stem]);
  const night = look.sky === 'night' || look.sky === 'dusk' || look.sky === 'dawn';
  const color = ANIMAL_COLOR[ds];
  const animalBright = Math.min(1, 0.55 + look.bright * 0.5);
  const animalFilter = `${db === 'sin' ? MONKEY_FILTER[ds] : ''} brightness(${animalBright})`.trim();
  const presentation = useMemo(() => run && !portrait && !fixed ? scenePresentation(run) : null, [run, portrait, fixed]);
  const activeView = presentation?.views.find((view) => view.id === viewId)
    ?? (presentation ? presentation.views[run.age % presentation.views.length] : null);
  const milestones = presentation?.milestone;
  const showSceneDescription = activeView && !['natal', 'age'].includes(activeView.renderer);

  const scene = (
    <div className={`scene ${fixed ? 'scene-fixed' : portrait ? 'scene-portrait' : 'scene-box'}${activeView ? ' scene-diorama' : ''}`}
      data-mode={activeView?.id} data-life-stage={presentation?.phase.id}
      style={{ background: ss.bg, '--scene-accent': activeView?.palette[0], '--scene-accent-soft': activeView?.palette[1] }} aria-hidden="true">
      {!portrait && <>
      {sceneOk && (
        <img className="layer scene-landscape-layer" alt="" src={`${base}/assets/scenes/${mb}.jpg`} onError={() => setSceneOk(false)}
          style={{ filter: `brightness(${look.bright}) saturate(${look.sat})` }} />
      )}
      <div className="layer" style={{ background: `rgba(${look.tint})`, mixBlendMode: 'multiply' }} />
      {night && (
        <div className="layer sky">
          {look.sky !== 'dawn' && star.map((s, i) => (
            <i key={i} style={{ left: `${s.x}%`, top: `${s.y}%`, width: s.r, height: s.r, opacity: s.o }} />
          ))}
          <b className="moon" style={{ opacity: look.sky === 'dawn' ? 0.5 : 1 }} />
        </div>
      )}
      {look.sky === 'sunset' && <div className="layer"><b className="sun" /></div>}
      <img className="layer" alt="" src={`${base}/assets/weather/${WEATHER_KEYS[ms]}.webp`} />
      <div className="layer" style={{ background: `radial-gradient(ellipse at 50% 45%, transparent 62%, rgba(${ss.vig},.22) 100%)` }} />
      </>}
      {activeView && <>
        <div className="scene-diorama-ground" />
        <div key={`${activeView.id}-${activeView.jobId ?? activeView.stage ?? activeView.role ?? activeView.kind}-${activeView.state ?? ''}-${activeView.variant}`} className="scene-diorama-prop"
          data-kind={activeView.kind} style={{ filter: `brightness(${animalBright})` }}>
          <MainSceneProps view={activeView} />
        </div>
        <SceneAccent kind={activeView.accent} />
        {showSceneDescription && <div className="scene-diorama-note">
          <span className="scene-note-label">{activeView.noteLabel}</span>
          <strong className="scene-note-title">{activeView.noteTitle}</strong>
          <span className="scene-note-detail">{activeView.noteDetail}</span>
        </div>}
      </>}
      <div className="animal" style={{ '--body': color.body, '--body-light': color.light,
        filter: `${animalFilter}${!portrait ? ' drop-shadow(0 3px 2px #58452929)' : ''}` }}
        dangerouslySetInnerHTML={{ __html: svg }} />
    </div>
  );
  if (!milestones) return scene;
  return (
    <div className="life-scene-wrap" style={{ '--scene-accent': activeView.palette[0], '--scene-accent-soft': activeView.palette[1] }}>
      <div className="scene-view-controls">
        {showSceneDescription && <div className="scene-view-heading">
          <span className="scene-view-eyebrow">{activeView.eyebrow}</span>
          <strong title={activeView.title}>{activeView.title}</strong>
          <small title={activeView.detail}>{activeView.detail}</small>
        </div>}
        {presentation.views.length > 1 && <div className="scene-view-tabs" role="group" aria-label="장면 보기">
          <button type="button" aria-pressed={!presentation.views.some((view) => view.id === viewId)}
            title="해가 바뀌면 기록이 있는 장면을 번갈아 보여줍니다." onClick={() => setViewId(null)}>자동</button>
          {presentation.views.map((view) => <button key={view.id} type="button"
            aria-pressed={view.id === viewId} onClick={() => setViewId(view.id)}>{view.label}</button>)}
        </div>}
      </div>
      {scene}
      <p className="scene-life-caption">
        {showSceneDescription && <span>{activeView.propLabels.join(' · ')}</span>}
        <span>학업 행동 {milestones.learningCount}년</span>
        <span>활동 분야 완료 {milestones.completedCount}회</span>
        <span>곁의 사람 {milestones.peopleCount}명</span>
      </p>
    </div>
  );
}

function MainSceneProps({ view }) {
  if (view.renderer === 'career') return <CareerObjects jobId={view.jobId} level={view.level} variant={view.variant} />;
  if (view.renderer === 'age') return <AgeObjects stage={view.stage} variant={view.variant} />;
  if (view.renderer === 'relationship') return <RelationshipObjects role={view.role} state={view.state} variant={view.variant} />;
  if (view.renderer === 'action') return <ActionObjects kind={view.kind} variant={view.variant} />;
  if (view.renderer === 'natal') return <NatalObjects labels={view.propLabels} variant={view.variant} />;
  return <SceneProps kind={view.kind} variant={view.variant} />;
}

function SceneAccent({ kind }) {
  return <div className="scene-diorama-accent">
    <svg viewBox="0 0 100 90" focusable="false">
      <ellipse cx="50" cy="80" rx="42" ry="6" fill="#504537" opacity=".13" />
      {kind === 'cups' ? <>
        <ellipse cx="50" cy="69" rx="37" ry="7" fill="#c0a47f" />
        <path d="M22 41h24v18c0 13-24 13-24 0z" fill="#f0dfbd" />
        <path d="M46 45h7c9 0 9 12 0 12h-7" fill="none" stroke="#f0dfbd" strokeWidth="5" />
        <path d="M59 43h20v16c0 12-20 12-20 0z" fill="#7b998d" />
        <path d="M79 47h5c8 0 8 10 0 10h-5" fill="none" stroke="#7b998d" strokeWidth="4" />
      </> : kind === 'plants' ? <>
        <path d="M30 48h40l-6 29H36z" fill="#b98768" />
        <path d="M50 53V21" stroke="#657e57" strokeWidth="4" />
        <ellipse cx="38" cy="33" rx="9" ry="16" transform="rotate(-43 38 33)" fill="#8aa376" />
        <ellipse cx="62" cy="23" rx="9" ry="16" transform="rotate(43 62 23)" fill="#779566" />
      </> : <>
        <rect x="15" y="58" width="61" height="13" rx="3" fill="#8c9c88" />
        <path d="M22 62h47M22 67h47" stroke="#e9dec7" strokeWidth="2" />
        <path d="M57 58V37h23v21" fill="#ba9672" />
        <path d="m64 39-5-21m11 21 1-25m4 25 8-19" stroke="#687f7d" strokeWidth="4" strokeLinecap="round" />
      </>}
    </svg>
  </div>;
}
